/**
 * Contexte d'authentification.
 *
 * Le jeton JWT est persiste dans `localStorage` afin de survivre a un
 * rechargement ; au demarrage il est revalide par `GET /auth/moi` plutot que
 * decode cote client (le client n'a pas a faire confiance a son propre jeton).
 */
import {
  Fragment,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { LoaderCircle } from 'lucide-react';
import type { ChangerMotDePasse, MajProfil, ReponseConnexionDTO, UtilisateurDTO } from '@leads/shared';
import {
  ErreurApi, appelApi, ecrireJeton, lireJeton, surChangementMotDePasseRequis, surJetonModifieAilleurs, surSessionExpiree,
} from './api';

export interface ValeurAuth {
  utilisateur: UtilisateurDTO | null;
  /** `true` tant que la revalidation initiale du jeton est en cours. */
  chargement: boolean;
  estConnecte: boolean;
  connexion(email: string, motDePasse: string): Promise<UtilisateurDTO>;
  /**
   * Change le mot de passe du titulaire. L'API revoque ses autres sessions et
   * rend un nouveau jeton, qui remplace celui-ci.
   */
  changerMotDePasse(corps: ChangerMotDePasse): Promise<UtilisateurDTO>;
  /** Met a jour le nom affiche ; la session suit sans rechargement. */
  majProfil(corps: MajProfil): Promise<UtilisateurDTO>;
  deconnexion(): void;
}

const ContexteAuth = createContext<ValeurAuth | null>(null);

export function FournisseurAuth({
  children,
  onDeconnexion,
}: {
  children: ReactNode;
  /** Permet de vider le cache react-query a la deconnexion. */
  onDeconnexion?: () => void;
}) {
  const [utilisateur, setUtilisateur] = useState<UtilisateurDTO | null>(null);
  const [chargement, setChargement] = useState<boolean>(() => lireJeton() !== null);

  // Evite de recreer les callbacks quand seule la fonction de nettoyage change.
  const refDeconnexion = useRef(onDeconnexion);
  refDeconnexion.current = onDeconnexion;

  const deconnexion = useCallback(() => {
    ecrireJeton(null);
    setUtilisateur(null);
    setChargement(false);
    refDeconnexion.current?.();
  }, []);

  /** Une session s'ouvre (connexion) ou se renouvelle (changement de mot de passe). */
  const ouvrirSession = useCallback((reponse: ReponseConnexionDTO): UtilisateurDTO => {
    ecrireJeton(reponse.token);
    setUtilisateur(reponse.utilisateur);
    setChargement(false);
    return reponse.utilisateur;
  }, []);

  const connexion = useCallback(
    async (email: string, motDePasse: string) =>
      ouvrirSession(
        await appelApi<ReponseConnexionDTO>('/api/v1/auth/connexion', {
          methode: 'POST',
          corps: { email, motDePasse },
          sansDeconnexion: true,
        }),
      ),
    [ouvrirSession],
  );

  const changerMotDePasse = useCallback(
    async (corps: ChangerMotDePasse) =>
      ouvrirSession(await appelApi<ReponseConnexionDTO>('/api/v1/auth/mot-de-passe', { methode: 'POST', corps })),
    [ouvrirSession],
  );

  const majProfil = useCallback(async (corps: MajProfil) => {
    const moi = await appelApi<UtilisateurDTO>('/api/v1/auth/moi', { methode: 'PATCH', corps });
    setUtilisateur(moi);
    return moi;
  }, []);

  // Revalidation du jeton au demarrage.
  useEffect(() => {
    if (lireJeton() === null) {
      setChargement(false);
      return;
    }
    let annule = false;
    void (async () => {
      try {
        const moi = await appelApi<UtilisateurDTO>('/api/v1/auth/moi', { sansDeconnexion: true });
        if (!annule) setUtilisateur(moi);
      } catch (cause) {
        // Seul un 401 dit le jeton perime. Une panne (reseau, 5xx) le garde :
        // un redemarrage de l'API ne doit pas deconnecter toute l'equipe, le
        // prochain chargement de la page rouvrira la session. Perime, il ferme
        // la session comme toute deconnexion : ce que le compte a laisse dans
        // l'onglet (session d'appels) part avec lui.
        if (!annule && cause instanceof ErreurApi && cause.estSessionExpiree) deconnexion();
      } finally {
        if (!annule) setChargement(false);
      }
    })();
    return () => {
      annule = true;
    };
  }, [deconnexion]);

  // Tout 401 renvoye par l'API purge la session.
  useEffect(() => surSessionExpiree(deconnexion), [deconnexion]);

  /*
   * Un autre onglet change le jeton partage : cet onglet suit. Deconnecte la,
   * la session se ferme ici ; ouverte sous un autre compte, elle est relue, et
   * le cache de l'ancien compte est vide (ses donnees n'ont plus a s'afficher).
   */
  const refUtilisateur = useRef(utilisateur);
  refUtilisateur.current = utilisateur;
  useEffect(
    () =>
      surJetonModifieAilleurs((jeton) => {
        if (jeton === null) {
          deconnexion();
          return;
        }
        void (async () => {
          try {
            const moi = await appelApi<UtilisateurDTO>('/api/v1/auth/moi', { sansDeconnexion: true });
            // Un autre changement a suivi : sa propre relecture l'emporte.
            if (lireJeton() !== jeton) return;
            if (refUtilisateur.current?.id !== moi.id) refDeconnexion.current?.();
            setUtilisateur(moi);
          } catch (cause) {
            if (cause instanceof ErreurApi && cause.estSessionExpiree) deconnexion();
          }
        })();
      }),
    [deconnexion],
  );

  // L'API exige le changement d'un mot de passe provisoire (403
  // CHANGEMENT_MOT_DE_PASSE_REQUIS) : RequiertAuth renvoie alors vers l'ecran dedie.
  useEffect(
    () =>
      surChangementMotDePasseRequis(() => {
        setUtilisateur((actuel) =>
          actuel && !actuel.doitChangerMotDePasse ? { ...actuel, doitChangerMotDePasse: true } : actuel,
        );
      }),
    [],
  );

  const valeur = useMemo<ValeurAuth>(
    () => ({
      utilisateur,
      chargement,
      estConnecte: utilisateur !== null,
      connexion,
      changerMotDePasse,
      majProfil,
      deconnexion,
    }),
    [utilisateur, chargement, connexion, changerMotDePasse, majProfil, deconnexion],
  );

  return <ContexteAuth.Provider value={valeur}>{children}</ContexteAuth.Provider>;
}

export function useAuth(): ValeurAuth {
  const valeur = useContext(ContexteAuth);
  if (!valeur) throw new Error('useAuth doit etre utilise dans un FournisseurAuth');
  return valeur;
}

/**
 * Adresse demandee avant un detour par la connexion ou par l'ecran de mot de
 * passe impose (`state.depuis`, pose par RequiertAuth), sinon l'accueil. Le
 * `state` d'une entree d'historique n'est pas type : il est verifie ici, en un
 * seul endroit.
 */
export function destinationMemorisee(emplacement: { state: unknown }): string {
  const { state } = emplacement;
  const depuis = typeof state === 'object' && state !== null && 'depuis' in state ? state.depuis : undefined;
  return typeof depuis === 'string' && estCheminInterne(depuis) ? depuis : '/';
}

/**
 * Chemin de l'application : une seule barre oblique en tete. Une seconde barre,
 * droite ou inversee, ferait lire au navigateur une adresse d'un autre site.
 */
function estCheminInterne(chemin: string): boolean {
  if (!chemin.startsWith('/')) return false;
  const suivant = chemin.charAt(1);
  return suivant !== '/' && suivant !== String.fromCharCode(92);
}

/**
 * Garde des ecrans authentifies. Sans session : la connexion, en memorisant
 * la destination. Avec un mot de passe provisoire : l'ecran de changement
 * impose, seul accessible. `pourChangementMotDePasse` marque cet ecran, qui
 * renvoie a son tour vers la destination une fois le mot de passe change.
 */
export function RequiertAuth({
  children,
  pourChangementMotDePasse = false,
}: {
  children: ReactNode;
  pourChangementMotDePasse?: boolean;
}) {
  const { utilisateur, chargement } = useAuth();
  const emplacement = useLocation();

  if (chargement) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-fond">
        <p className="flex items-center gap-2 text-sm text-encre-discrete" role="status">
          <LoaderCircle aria-hidden="true" className="animate-spin" />
          Vérification de la session…
        </p>
      </div>
    );
  }

  const ici = emplacement.pathname + emplacement.search;
  if (!utilisateur) return <Navigate to="/connexion" state={{ depuis: ici }} replace />;

  if (utilisateur.doitChangerMotDePasse && !pourChangementMotDePasse) {
    return <Navigate to="/mot-de-passe" state={{ depuis: ici }} replace />;
  }
  if (!utilisateur.doitChangerMotDePasse && pourChangementMotDePasse) {
    return <Navigate to={destinationMemorisee(emplacement)} replace />;
  }
  // Un compte par arbre : ouvert dans un autre onglet, un autre compte remonte les ecrans, dont l'etat local
  // (session d'appels, message du jour) ne passe jamais d'un compte a l'autre.
  return <Fragment key={utilisateur.id}>{children}</Fragment>;
}
